import { useEffect, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackVisitorIntent } from '@/lib/utils/analytics';
import { captureResearchLead } from '@/lib/firebase/researchLeads';

const STORAGE_KEY = 'sf_intent_responded';
const SHOW_DELAY_MS = 12000;

type Intent = 'creator' | 'supporter' | 'researching';

const OPTIONS: { value: Intent; label: string }[] = [
  { value: 'creator', label: 'Set up my own fund page' },
  { value: 'supporter', label: 'Support an athlete I know' },
  { value: 'researching', label: 'Just researching for now' },
];

/**
 * Low-friction top-of-funnel feedback capture: most visitors never sign up, so this is
 * currently the only qualitative signal we get from them at all (donation/publish-stall
 * feedback only fires deeper in the funnel). Timer-gated well past the prerender snapshot
 * (fires on 'render-event'/8s safety net, see vite.config.ts) so it never appears in the
 * static SEO HTML.
 */
export default function VisitorIntentPrompt() {
  const [visible, setVisible] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [answeredIntent, setAnsweredIntent] = useState<Intent | null>(null);
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(STORAGE_KEY)) return;

    const timer = window.setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const respond = (intent: Intent | 'dismissed') => {
    if (answered || showEmailCapture) return;
    trackVisitorIntent(intent);
    window.localStorage.setItem(STORAGE_KEY, '1');
    if (intent === 'researching') {
      // Researchers rarely sign up on this visit — offer a low-friction way to stay
      // reachable instead of just logging the intent and losing them for good.
      setShowEmailCapture(true);
      return;
    }
    setAnswered(true);
    setAnsweredIntent(intent === 'dismissed' ? null : intent);
    if (intent !== 'creator') {
      window.setTimeout(() => setVisible(false), 1600);
    }
  };

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await captureResearchLead(email);
    } catch {
      /* noop — best-effort capture, don't block the visitor on it */
    }
    setEmailSubmitted(true);
    window.setTimeout(() => setVisible(false), 1600);
  };

  if (!visible) return null;

  return (
    // z-[110]: must clear ConsentBanner's z-[100] (also bottom-anchored) or its
    // option buttons render underneath the still-open banner and are unclickable —
    // matches this run's PostHog read of 2/2 responses being "dismissed" (the only
    // control left reachable above the banner is the X).
    <div className="fixed bottom-5 right-5 z-[110] w-[calc(100vw-2.5rem)] max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl shadow-black/10 animate-in fade-in slide-in-from-bottom-4">
      <button
        type="button"
        onClick={() => respond('dismissed')}
        aria-label="Close"
        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
      >
        <X className="h-4 w-4" />
      </button>
      {emailSubmitted ? (
        <p className="pr-4 text-sm font-medium text-slate-700">Thanks — we'll follow up.</p>
      ) : showEmailCapture ? (
        <form onSubmit={submitEmail}>
          <p className="pr-4 text-sm font-semibold text-slate-900">Want tips on fundraising for your sport sent to your inbox?</p>
          <div className="mt-3 flex flex-col gap-2">
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-900 outline-none focus:border-blue-300"
            />
            <button
              type="submit"
              className="rounded-full bg-primary/90 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-800"
            >
              Keep me posted
            </button>
            <button
              type="button"
              onClick={() => setVisible(false)}
              className="text-center text-xs font-medium text-slate-400 hover:text-slate-600"
            >
              No thanks
            </button>
          </div>
        </form>
      ) : answered && answeredIntent === 'creator' ? (
        <div className="pr-4">
          <p className="text-sm font-medium text-slate-700">Great — let's get your page started.</p>
          <Link
            to="/signup-creator"
            onClick={() => setVisible(false)}
            className="mt-3 inline-block rounded-full bg-blue-700 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-800"
          >
            Set up my fund page
          </Link>
        </div>
      ) : answered ? (
        <p className="pr-4 text-sm font-medium text-slate-700">Thanks — that helps us build the right thing.</p>
      ) : (
        <>
          <p className="pr-4 text-sm font-semibold text-slate-900">What brings you to Sport Fund today?</p>
          <div className="mt-3 flex flex-col gap-2">
            {OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => respond(opt.value)}
                className="rounded-full border border-slate-200 px-4 py-2 text-left text-sm font-medium text-slate-700 hover:border-blue-300 hover:bg-primary/10 hover:text-blue-800 transition-colors"
              >
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
